/**
 * @file Single-source literal atoms for values shared across multiple public/*.ts files.
 * Domain modules import these instead of re-declaring the same string/number literal.
 * Keeps `pnpm check:constants:overlap` clean.
 */
/** Shared numeric atoms. */
export const LITERAL_NUM = Object.freeze({
    N0: 0,
    N1: 1,
    N10: 10,
    N100: 100,
    N10000: 10000,
    N12: 12,
    N120: 120,
    N128: 128,
    N15: 15,
    N150: 150,
    N2: 2,
    N20: 20,
    N200: 200,
    N2000: 2000,
    N24: 24,
    N255: 255,
    N3: 3,
    N30: 30,
    N300: 300,
    N3600: 3600,
    N4: 4,
    N40: 40,
    N5: 5,
    N50: 50,
    N500: 500,
    N5000: 5000,
    N6: 6,
    N60: 60,
    N7: 7,
    N8: 8,
    N8000: 8000,
    N900: 900,
});
/** Shared string atoms. */
export const LITERAL_STR = Object.freeze({
    X_059669: '#059669',
    X_0F172A: '#0f172a',
    X_10B981: '#10b981',
    E2E8F0: '#e2e8f0',
    F59E0B: '#f59e0b',
    X_91: '+91',
    PDF_IMAGE: '.pdf,image/*',
    X_18_GST: '18% GST',
    X_2BHK: '2BHK',
    A_UNIT_CAN_SPAN_AT_MOST_THREE_CONSECUTIVE_FLOORS: 'A unit can span at most three consecutive floors.',
    AI: 'AI',
    AI_ACCESS_NO_BILLING: 'AI_ACCESS_NO_BILLING',
    AI_AGREEMENT_BILLING_FAILED: 'AI_AGREEMENT_BILLING_FAILED',
    AI_AGREEMENT_BYOK_EXHAUSTED: 'AI_AGREEMENT_BYOK_EXHAUSTED',
    AI_AGREEMENT_GENERATION_FAILED: 'AI_AGREEMENT_GENERATION_FAILED',
    AI_AGREEMENT_IDEMPOTENCY_CONFLICT: 'AI_AGREEMENT_IDEMPOTENCY_CONFLICT',
    AI_AGREEMENT_INSUFFICIENT_CREDITS: 'AI_AGREEMENT_INSUFFICIENT_CREDITS',
    AI_AGREEMENT_INVALID_MODE: 'AI_AGREEMENT_INVALID_MODE',
    AI_AGREEMENT_INVALID_PROVIDER_MODEL: 'AI_AGREEMENT_INVALID_PROVIDER_MODEL',
    AI_AGREEMENT_STATIC_CONFIG_UNAVAILABLE: 'AI_AGREEMENT_STATIC_CONFIG_UNAVAILABLE',
    AI_CREDENTIAL_TEST_FAILED: 'AI_CREDENTIAL_TEST_FAILED',
    AI_NOT_ENABLED: 'AI_NOT_ENABLED',
    ACTION: 'Action',
    ACTIONS: 'Actions',
    ACTIVE: 'Active',
    ACTIVITY: 'Activity',
    ACTOR: 'Actor',
    ADD_BUILDING: 'Add building',
    ADD_ROW: 'Add row',
    ADD_ROWS_BELOW_AND_SUBMIT_WITHOUT_DOWNLOADING_A_: 'Add rows below and submit without downloading a spreadsheet.',
    ADD_UNIT: 'Add unit',
    ADD_WING: 'Add wing',
    AGREEMENT_SENT_FOR_SIGNING: 'Agreement sent for signing.',
    AGREEMENT_TEMPLATE: 'Agreement template',
    AGREEMENT_TEMPLATES: 'Agreement templates',
    AGREEMENT_TERM: 'Agreement term',
    ALL: 'All',
    ALL_RESIDENTS: 'All residents',
    ALL_STATUSES: 'All statuses',
    ALL_UNITS: 'All units',
    ALL_WINGS: 'All wings',
    AMBER: 'Amber',
    AMOUNT: 'Amount',
    ARCHIVE: 'Archive',
    ARCHIVED: 'Archived',
    AREA_SQ_FT: 'Area (sq ft) *',
    AUDIENCE: 'Audience',
    BUILDING_STAFF: 'BUILDING_STAFF',
    BACK: 'Back',
    BATHROOMS: 'Bathrooms',
    BEDROOMS: 'Bedrooms',
    BILLING: 'Billing',
    BILLING_COUNTRY: 'Billing country',
    BLUE: 'Blue',
    BUILDING: 'Building',
    BUILDING_NAME: 'Building name',
    BUILDING_STAFF_2: 'Building staff',
    BUILDINGS: 'Buildings',
    CALCULATING_IMPACT: 'Calculating impact…',
    CANCEL: 'Cancel',
    CANCELLED: 'Cancelled',
    CATEGORY: 'Category',
    CITY: 'City',
    CLEANLINESS: 'Cleanliness',
    CLEAR: 'Clear',
    CLOSE: 'Close',
    COMMERCIAL: 'Commercial',
    COMPLAINT: 'Complaint',
    COMPLAINT_REJECTED: 'Complaint rejected',
    COMPLAINT_RESOLVED: 'Complaint resolved',
    COMPLETE_YOUR_PROFILE: 'Complete your profile',
    COMPLETE_YOUR_RESIDENT_PROFILE: 'Complete your resident profile',
    COMPLETED: 'Completed',
    CONDITIONAL_RULES: 'Conditional rules',
    CONTACT: 'Contact',
    CONTINUE: 'Continue',
    CONTRACT: 'Contract',
    COULD_NOT_READ_THIS_SPREADSHEET_TRY_DOWNLOADING_: 'Could not read this spreadsheet. Try downloading it instead.',
    COUNTRY: 'Country',
    CREATE: 'Create',
    CREATED: 'Created',
    CREATING: 'Creating…',
    CURRENCY: 'Currency',
    CUSTOM: 'Custom',
    DASHBOARD: 'Dashboard',
    DEACTIVATE: 'Deactivate',
    DEFAULT: 'Default',
    DELETE: 'Delete',
    DELETE_DOCUMENT: 'Delete document?',
    DELETE_SELECTED: 'Delete selected',
    DELETING: 'Deleting…',
    DESCRIPTION: 'Description',
    DISCARD: 'Discard',
    DISCARD_CHANGES: 'Discard changes?',
    DOCUMENT_TYPE: 'Document type',
    DOCUMENT_TYPE_IS_REQUIRED: 'Document type is required.',
    DOCUMENTS: 'Documents',
    DORMANT: 'Dormant',
    DOWNLOAD: 'Download',
    DOWNLOAD_ERROR_REPORT: 'Download error report',
    DRIVING_LICENSE: 'Driving license',
    EDIT: 'Edit',
    EDIT_MODE: 'Edit mode',
    EMAIL: 'Email *',
    EMAIL_ADDRESS: 'Email address',
    EMERGENCY_CONTACT_NAME: 'Emergency contact name',
    EMPLOYMENT: 'Employment',
    EMPLOYMENT_TYPE: 'Employment type',
    EMPLOYMENT_TYPE_IS_REQUIRED: 'Employment type is required.',
    ENABLED: 'Enabled',
    ENTER_A_VALID_EMAIL_ADDRESS: 'Enter a valid email address.',
    ENTER_A_VALID_MOBILE_NUMBER: 'Enter a valid mobile number.',
    ENVIRONMENT_OVERRIDE: 'Environment override',
    ERROR: 'Error',
    ERROR_CODE: 'Error code',
    EXIT_FULL_SCREEN: 'Exit full screen',
    FEATURE_DISABLED: 'FEATURE_DISABLED',
    FAILED: 'Failed',
    FAILED_IMPORTS: 'Failed imports',
    FAILED_PAYMENTS: 'Failed payments',
    FAILURE_REASON: 'Failure reason',
    FIELD_GUIDE: 'Field guide',
    FILL_IN_APP: 'Fill in app',
    FIX_FAILED_ROWS: 'Fix failed rows',
    FLOOR: 'Floor',
    FULL_NAME: 'Full name',
    FULL_NAME_IS_REQUIRED: 'Full name is required.',
    FULL_SCREEN: 'Full screen',
    FURNISHING_STATUS: 'Furnishing Status',
    GENDER: 'Gender',
    GENERATE_AGREEMENT: 'Generate agreement',
    GENERATING: 'Generating…',
    GREEN: 'Green',
    HALLS: 'Halls',
    HELLO: 'Hello,',
    HIGH: 'High',
    IN: 'IN',
    IND: 'IND',
    INR: 'INR',
    IDLE: 'Idle',
    IMPORT_FROM_EXCEL: 'Import from Excel',
    IMPORTING: 'Importing…',
    IN_PROGRESS: 'In progress',
    INACTIVE: 'Inactive',
    INDIA: 'India',
    INTERNAL_NOTES: 'Internal notes',
    INVOICE: 'Invoice',
    KEEP_EDITING: 'Keep editing',
    KITCHENS: 'Kitchens',
    LEASE_NOT_OWNED: 'LEASE_NOT_OWNED',
    LEASE_NOT_RENEWABLE: 'LEASE_NOT_RENEWABLE',
    LEASE_RENEWAL_ALREADY_OPEN: 'LEASE_RENEWAL_ALREADY_OPEN',
    LEASE_RENEWAL_NOT_FOUND: 'LEASE_RENEWAL_NOT_FOUND',
    LAST_7_DAYS: 'Last 7 days',
    LEASE_EXPIRING_SOON: 'Lease expiring soon',
    LEASE_TERMS: 'Lease terms',
    LOADING: 'Loading…',
    LOW: 'Low',
    MRR: 'MRR',
    MAINTENANCE: 'Maintenance',
    MAINTENANCE_REQUEST: 'Maintenance request',
    MAINTENANCE_REQUESTS: 'Maintenance requests',
    MAINTENANCE_WORKER: 'Maintenance worker',
    MAINTENANCE_WORKERS: 'Maintenance workers',
    MANAGE_API_KEYS: 'Manage API keys',
    MANAGE_UNITS: 'Manage units',
    MEDIUM: 'Medium',
    MOBILE: 'Mobile',
    MOBILE_NUMBER: 'Mobile number',
    MOBILE_NUMBER_IS_REQUIRED: 'Mobile number is required.',
    MODE: 'Mode',
    MODEL: 'Model',
    MORE: 'More',
    NAME: 'Name',
    NAME_MUST_MATCH_EXACTLY_INCLUDING_SPACES_AND_CAP: 'Name must match exactly — including spaces and capitalization.',
    NAVY: 'Navy',
    NEXT: 'Next',
    NO_PENDING_IMPORT_FAILURES: 'No pending import failures',
    NO_UNITS_MATCH_YOUR_SEARCH: 'No units match your search.',
    NO_WINGS_MATCH_YOUR_SEARCH: 'No wings match your search.',
    NONE: 'None',
    NOTIFICATION: 'Notification',
    OWNER: 'OWNER',
    OCCUPATION: 'Occupation',
    OFF: 'Off',
    ON: 'On',
    ONE_OR_MORE_WINGS_DO_NOT_BELONG_TO_THIS_BUILDING: 'One or more wings do not belong to this building.',
    OPEN: 'Open',
    OPEN_AI_SETTINGS: 'Open AI settings',
    OPEN_AGREEMENT_STUDIO: 'Open Agreement Studio',
    OPERATIONS: 'Operations',
    OPTIONAL: 'Optional',
    OTHER: 'Other',
    OTHERS: 'Others',
    OVERVIEW: 'Overview',
    OWNER_2: 'Owner',
    PAGINATED_LIST: 'PAGINATED_LIST',
    PAN: 'PAN',
    PARKING: 'Parking',
    PARKING_SLOTS: 'Parking slots',
    PASSWORD_UPDATED_SUCCESSFULLY: 'Password updated successfully.',
    PAYMENT_METHOD: 'Payment method',
    PAYMENT_RECEIVED: 'Payment received',
    PENDING: 'Pending',
    PENDING_ROWS: 'Pending rows',
    PERMANENT_ADDRESS: 'Permanent address',
    PERMISSION_GROUP: 'Permission group',
    PHOTO: 'Photo',
    PICK_FROM_DROPDOWN: 'Pick from dropdown',
    PLAN: 'Plan',
    PLANS: 'Plans',
    PLATFORM: 'Platform',
    PLATFORM_CATALOG: 'Platform catalog',
    PLATFORM_CATALOG_READ_ONLY: 'Platform catalog — read-only',
    PREVIEW: 'Preview',
    PREVIEW_ERROR_REPORT: 'Preview error report',
    PREVIOUS: 'Previous',
    PRIORITY: 'Priority',
    PROFILE_PHOTO: 'Profile photo',
    PROPBIX: 'PropBix',
    PROPERTY: 'Property',
    PROVIDER: 'Provider',
    REACTIVATE: 'Reactivate',
    RECEIVE_BROWSER_PUSH_NOTIFICATIONS: 'Receive browser push notifications',
    RECEIVE_NOTIFICATIONS_VIA_EMAIL: 'Receive notifications via email',
    RECENT_ACTIVITY: 'Recent activity',
    RECENTLY_CREATED: 'Recently created',
    RECENTLY_UPDATED: 'Recently updated',
    REFERENCE: 'Reference',
    REGION: 'Region',
    REJECT: 'Reject',
    REJECTED: 'Rejected',
    REMAINING: 'Remaining',
    REMOVE: 'Remove',
    RENT_CONFIGURATION: 'Rent configuration',
    RENT_CURRENCY: 'Rent currency',
    RENT_DUE_DAY_OF_MONTH: 'Rent due day of month',
    RENTAL_AGREEMENT: 'Rental agreement',
    RENTAL_AGREEMENTS: 'Rental agreements',
    RENTAL_DEFAULTS: 'Rental defaults',
    REQUEST_ID: 'Request ID',
    REQUIRED: 'Required',
    RESIDENT: 'Resident',
    RESIDENTIAL: 'Residential',
    RESIDENTS: 'Residents',
    REVIEW_FAILED_IMPORTS: 'Review failed imports',
    ROLE: 'Role',
    ROSE: 'Rose',
    SAVE: 'Save',
    SAVE_CHANGES: 'Save Changes',
    SAVE_CHANGES_2: 'Save changes',
    SAVING: 'Saving…',
    SCHEDULED_FOR_DELETION: 'Scheduled for deletion',
    SCHEDULING: 'Scheduling…',
    SEARCH: 'Search',
    SEARCH_BY_WING_OR_UNIT_NUMBER: 'Search by wing or unit number…',
    SEARCH_RESULTS: 'Search results',
    SEARCH_WINGS: 'Search wings…',
    SEARCH_2: 'Search…',
    SECURITY: 'Security',
    SECURITY_DEPOSIT: 'Security deposit',
    SECURITY_DEPOSIT_2: 'Security deposit *',
    SELECT_ALL: 'Select all',
    SELECT_WING: 'Select wing',
    SEND_FOR_SIGNING: 'Send for signing',
    SEND_NOTIFICATION: 'Send notification',
    SENDING: 'Sending…',
    SETTINGS: 'Settings',
    SHARED: 'Shared',
    SKY: 'Sky',
    SORT_BY: 'Sort by',
    SOURCE: 'Source',
    SOURCE_ROW: 'Source row',
    STATUS: 'Status',
    STATUS_UPDATED: 'Status updated',
    STORAGE: 'Storage',
    SUBMIT_CORRECTIONS: 'Submit corrections',
    SUCCEEDED: 'Succeeded',
    SYSTEM: 'System',
    TABLE: 'Table',
    TEMPLATE: 'Template',
    TENANT: 'Tenant',
    TENANTS: 'Tenants',
    TERM_START_DATE: 'Term start date',
    THIS_SHEET_HAS_NO_DATA: 'This sheet has no data.',
    THIS_STATUS_CHANGE_IS_NOT_ALLOWED: 'This status change is not allowed.',
    TITLE: 'Title',
    TOTAL: 'Total',
    TRACK_AND_MANAGE_MAINTENANCE_REQUESTS_ACROSS_THE: 'Track and manage maintenance requests across the property.',
    TRY_AGAIN: 'Try again',
    TYPE: 'Type',
    UPI: 'UPI',
    US: 'US',
    USD: 'USD',
    UNIT: 'Unit',
    UNIT_FEATURES: 'Unit features',
    UNIT_KIND: 'Unit kind',
    UNIT_NUMBER: 'Unit number',
    UNIT_NUMBER_2: 'Unit number *',
    UNIT_TYPE: 'Unit type',
    UNIT_TYPES: 'Unit types',
    UNITS: 'Units',
    UNSAVED_DETAILS_WILL_BE_LOST_IF_YOU_LEAVE_NOW: 'Unsaved details will be lost if you leave now.',
    UNSAVED_EDITS_WILL_BE_LOST_IF_YOU_LEAVE_NOW: 'Unsaved edits will be lost if you leave now.',
    UPDATED: 'Updated',
    UPLOAD_EXCEL: 'Upload Excel',
    UPLOAD_DOCUMENT: 'Upload document',
    USE_CANCEL_TO_CLOSE_UNSAVED_CHANGES_WILL_BE_LOST: 'Use Cancel to close. Unsaved changes will be lost.',
    USER: 'User',
    VIEW: 'View',
    VIEW_MODE: 'View mode',
    WHAT_HAPPENS_NEXT: 'What happens next',
    WHEN: 'When',
    WHEN_UNIT_ASSIGNED: 'When unit assigned',
    WING: 'Wing',
    WING_NAME: 'Wing name',
    WINGS: 'Wings',
    AGREEMENT_TEMPLATES_2: 'agreement-templates',
    AIBILLINGOBSERVABILITY: 'aiBillingObservability',
    AMENITYTYPES: 'amenityTypes',
    ANNUALESCALATIONPERCENT: 'annualEscalationPercent',
    APPCONFIGURATION: 'appConfiguration',
    AUDIT_LOGS: 'audit-logs',
    AUDITLOG: 'auditLog',
    BILLINGOBSERVABILITY: 'billingObservability',
    BILLINGREVENUE: 'billingRevenue',
    BUILDINGID: 'buildingId',
    CREATEDAT: 'createdAt',
    ESCALATEANCHOR: 'escalateAnchor',
    ESCALATEDAY: 'escalateDay',
    ESCALATEMONTH: 'escalateMonth',
    FAILED_IMPORTS_2: 'failed-imports',
    FEATUREFLAGS: 'featureFlags',
    INVENTORYDISPOSITION: 'inventoryDisposition',
    INVITETENANTUSER: 'inviteTenantUser',
    JOBMONITORING: 'jobMonitoring',
    LASTLOGINAT: 'lastLoginAt',
    MAINTENANCE_WORKERS_2: 'maintenance-workers',
    RESIDENT_EXAMPLE_COM: 'resident@example.com',
    TARGETMONTHLYRENT: 'targetMonthlyRent',
    UNITNUMBER: 'unitNumber',
    WINGNAME: 'wingName',
    CANCEL_ACTIVE_AGREEMENTS_BEFORE_REMOVE_RESIDENT: 'Cancel active agreements and end unit leases before removing this resident.',
    BULK_IMPORT_FAILED_ROWS_HERO_SUBTITLE: 'Fix validation issues inline or in the spreadsheet, then submit. Rows stay here until they import successfully or you remove them.',
    BULK_IMPORT_FAILED_ROWS_EMPTY_DESCRIPTION: 'When a bulk Excel import partially fails, those rows appear here so you can fix them anytime — even after closing the import dialog.',
    BUILDING_NO_WINGS_YET: 'This building has no wings yet. Add a wing under Building details, then try again.',
    PROPERTY_DELETION_NOTIFICATION_ON_COMPLETE: 'You will receive an in-app notification when deletion completes or fails.',
});
